import { GroupDownloadDto, LanguageStringDownloadDto, MediaDownloadDto, MediaUploadDto } from 'collaboration-service';
import useCategoryTypeSelection from "components/CategorySelection/useCategoryTypeSelection";
import GroupSelect from 'components/General/GroupSelect/GroupSelect';
import InfoBox from 'components/InfoBox/InfoBox';
import LabelInfo from "components/LabelInfo";
import { MediaType } from 'components/MediaSelection/FileSelector';
import MediaSelection from 'components/MediaSelection/MediaSelection';
import { EditLanguageStringsBase } from "components/PostEdit/Editors/EditLanguageStrings";
import { defEditorFormats } from "components/PostEdit/TypeImplementations/PostEdit";
import { Flag, ISO_3166, Icon, Theme, useBelowBreakpointOrEqual, useThemePart } from 'imaginarity-react-ui';
import { rootElement } from 'index';
import * as _ from "lodash";
import * as React from 'react';
import { ApplicationState, useAppSelector } from 'services/ApplicationState/ApplicationState';
import { removeHtmlTags, shallowCompare } from 'services/Helpers';
import { getTranslated } from 'services/Helpers/TranslationHelpers';
import { useImgI18N } from 'services/ImgI18N';
import { adminGroupsOfType } from 'services/StoreDependantHelpers';
import { CMSSC } from "views/CMS/CMSSC";
import { PlaylistEditState, useSelectorPLE } from './PlaylistEdit';
import { AGCInput, AGCInputDate, AGCInputTextArea, AGCTitle, GroupSelectContainer } from './PlaylistEditClasses';
import { PlaylistEditSequenceLinearSC as T } from "./PlaylistEditSequenceLinearSC";
import { usePLEState } from './usePLEState';
import SwitchButton from 'components/Switch/SwitchButton';

const mapper = (state: ApplicationState) => ({ i18n: state.i18n, contentLanguage: state.contentLanguage, routeParams: state.params, missing: state.playlistsState.missing, groups: state.userGroups });

export interface PlaylistEditGeneralProps {

}

const pleMapper = (state: PlaylistEditState) => ({
    expertMode: state.expertMode,
})

const PlaylistEditGeneral = (p: PlaylistEditGeneralProps) => {
    const { expertMode } = useSelectorPLE(pleMapper, shallowCompare);
    const isMobile = useBelowBreakpointOrEqual("tablet");

    const s_groupId = usePLEState(x => x.group_id);
    const s_published = usePLEState(x => x.hidden);
    const s_disallowRatings = usePLEState(x => x.forbidRatings);
    const s_allowComments = usePLEState(x => x.allowComments);
    const s_headlines = usePLEState(x => x.headlines);
    const s_descriptions = usePLEState(x => x.descriptions);
    const s_categories = usePLEState(x => x.categories);
    const s_media = usePLEState(x => x.media);
    // const config = useFrontEndConfig();

    const { /*contentLanguage,*/ groups, missing, routeParams } = useAppSelector(mapper, shallowCompare);
    const { t/*, currentLanguage*/ } = useImgI18N("playlist");
    const cHeight = useThemePart((t: Theme) => t.content.height) - 137;

    const edit = routeParams?.id !== undefined && routeParams?.id !== "";

    const missingGroup = s_groupId.value.length === 0;
    let date = new Date();
    date.setFullYear(date.getFullYear() + 20);
    // const lng = contentLanguage ?? currentLanguage;

    const setGroup = (group?: GroupDownloadDto) => {
        s_groupId.setValue(group?.id ?? "");
    }

    const togglePublished = () => {
        s_published.setValue(val => !val);
    }

    const toggleDisallowRatings = () => {
        s_disallowRatings.setValue(val => !val);
    }

    const toggleAllowComments = () => {
        s_allowComments.setValue(val => !val);
    }

    const updateHeadlines = (val: LanguageStringDownloadDto[] | ((val: LanguageStringDownloadDto[]) => LanguageStringDownloadDto[])) => {
        s_headlines.setValue(val);
    }
    const updateDescriptions = (val: LanguageStringDownloadDto[] | ((val: LanguageStringDownloadDto[]) => LanguageStringDownloadDto[])) => {
        s_descriptions.setValue(val as any);
    }


    const hasEnGbTitleEntry = _.some(s_headlines.value, n => n.lng === "en-GB");
    const hasEnGbDescEntry = _.some(s_descriptions.value, n => n.lng === "en-GB");


    const [/*catError*/, setCatError] = React.useState<{ msg: string, data: any }>();


    const { toRender, selectedCategories } =
        useCategoryTypeSelection({
            grouptype: "CMS",
            type: "normal",
            onError: setCatError,
            selectedCategories: s_categories.value,
            lng: "en-GB",
        });

    React.useEffect(() => {
        if (selectedCategories === undefined || selectedCategories.length === 0)
            return;
        const cIds = _.map(s_categories.value, c => c.id);
        const nIds = _.map(selectedCategories, c => c.id);
        if (!_.isEqual(cIds, nIds)) {
            s_categories.setValue(selectedCategories ?? []);
        }
    }, [selectedCategories, s_categories]);
    const containerRef = React.useRef<HTMLDivElement>(null);
    const [containerWidth, setContainerWidth] = React.useState<number>(500); // Default-Wert (z.B. 500)

    React.useEffect(() => {
        if (!containerRef.current) return;

        const observer = new ResizeObserver(entries => {
            for (let entry of entries) {
                setContainerWidth(entry.contentRect.width);
            }
        });

        observer.observe(containerRef.current);

        return () => {
            observer.disconnect(); // Cleanup, um Memory-Leaks zu vermeiden
        };
    }, []);

    const setMedia = React.useMemo(() => (file: File | undefined, media: MediaUploadDto) => {
        (media as any).file = file ? file : null;
        s_media.addFile();
        s_media.setValue(media as MediaDownloadDto);
    }, [s_media]);

    const avlWidth = containerWidth;
    return (
        <T.ImplContainer height={cHeight} style={{ paddingBottom: 0, paddingRight: 20 }}>
            <div style={{ display: "grid", gridTemplateColumns: "minmax(147px, max-content) 1fr", columnGap: 10, width: "100%", rowGap: 5 }}>
                {!expertMode &&
                    <div style={{ gridColumnStart: 2, gridColumnEnd: 3 }}>
                        <InfoBox type='info' content={t("more general setting options in expert mode (activate on top right button)")} icon='info' noBold noLetterSpacing noUppercase closeable />
                    </div>
                }
                <div>
                    <AGCTitle missingInput={missingGroup} >
                        {t("group")}
                        {missingGroup &&
                            <Icon name="asterisk" color="@accent" marginTop={0} style={{ marginTop: 5, marginLeft: 3, marginRight: 5, float: "right" }} />
                        }
                    </AGCTitle>
                </div>
                <div>
                    <AGCInput>
                        <GroupSelectContainer className="GroupSelectContainer" style={{ pointerEvents: edit ? "none" : undefined }}>
                            <GroupSelect
                                groups={adminGroupsOfType(["WIKI", "PLAYLIST"])}
                                onChange={setGroup}
                                disabled={edit}
                                value={_.find(groups, g => g.id === s_groupId.value)}
                            />
                        </GroupSelectContainer>
                    </AGCInput>
                </div>
                <div>
                    <AGCTitle missingInput={missing?.headline}>
                        {s_headlines.value.length > 0 &&
                            <LabelInfo
                                infoText=""
                                warning={false}
                                tooltipContent={<>
                                    <div style={{
                                        fontWeight: "bolder", lineHeight: "20px", textAlign: "left", padding: 10, background: "rgba(0,0,0,0.05)", width: "calc(100% + 26px)", transform: "translate(-13px, -13px)"
                                    }}>
                                        <Icon name="info" style={{ float: "left", marginRight: 10 }} marginTop={2} />
                                        {t("language variants")}
                                    </div>
                                    <div style={{ textAlign: "left", marginBottom: 10 }}>
                                        {t("titles are translated into the languages listed. if a language lacks a title, english or the first entered language is used as a fallback.")}
                                    </div>
                                    <div style={{
                                        display: "grid",
                                        gridTemplateColumns: "20px 1fr",
                                        columnGap: 10,
                                        gridTemplateRows: "max-content",
                                        textAlign: "left",
                                        overflow: "hidden"
                                    }}>
                                        {_.map(s_headlines.value, (n, j) => {
                                            const title = removeHtmlTags(n.text);
                                            const flag = <Flag hoverable={false} size={18} marginTop={0} name={n.lng as ISO_3166} />;
                                            const isFallback = hasEnGbTitleEntry ? n.lng === "en-GB" : j === 0;

                                            return (
                                                <React.Fragment key={j}>
                                                    {title !== undefined && title !== "" &&
                                                        <>
                                                            <T.TTFlag>{flag}</T.TTFlag>
                                                            <T.TTText style={{ fontWeight: isFallback ? "bolder" : "normal" }}>
                                                                {isFallback &&
                                                                    <Icon name="arrow right" style={{ float: "left", marginRight: 5 }} marginTop={1} size={12} color="@accentBlack" />
                                                                }
                                                                {title}
                                                            </T.TTText>
                                                        </>
                                                    }
                                                </React.Fragment>
                                            );
                                        })}


                                    </div>
                                </>}
                            />
                        }
                        {t("title")}
                        {missing?.headline &&
                            <Icon name="asterisk" color={"@accentRed"} marginTop={0} style={{ marginTop: 5, marginLeft: 3, marginRight: 5, float: "right" }} />
                        }
                    </AGCTitle>
                </div>
                <div>
                    <div ref={containerRef} style={{ marginBottom: 5 }}>
                        <EditLanguageStringsBase
                            value={s_headlines.value}
                            setValue={updateHeadlines}
                            editMode={true}
                            editorFormats={[]}
                            type="headlines"
                            noHtml
                            asInput
                            hideToolbar
                            fixWidth={avlWidth}
                            initialOpen={!edit}
                            hideTitleBarIfEmpty
                            menuPortal={rootElement}
                            savingText={t('confirm title')}
                            savingIcon='check'
                        />
                    </div>
                </div>
                <div>
                    <AGCTitle missingInput={missing?.description}>
                        {s_descriptions.value && s_descriptions.value.length > 0 &&
                            <LabelInfo
                                infoText=""
                                warning={false}
                                tooltipContent={<>
                                    <div style={{
                                        fontWeight: "bolder", lineHeight: "20px", textAlign: "left", padding: 10, background: "rgba(0,0,0,0.05)", width: "calc(100% + 26px)", transform: "translate(-13px, -13px)"
                                    }}>
                                        <Icon name="info" style={{ float: "left", marginRight: 10 }} marginTop={2} />
                                        {t("language variants")}
                                    </div>
                                    <div style={{ textAlign: "left", marginBottom: 10 }}>
                                        {t("descriptions are translated into the languages listed. if a language lacks a description, english or the first entered language is used as a fallback.")}
                                    </div>
                                    <div style={{
                                        display: "grid",
                                        gridTemplateColumns: "20px 1fr",
                                        columnGap: 10,
                                        gridTemplateRows: "max-content",
                                        textAlign: "left",
                                        overflow: "hidden"
                                    }}>
                                        {_.map(s_descriptions.value, (n, j) => {
                                            const desc = removeHtmlTags(n.text);
                                            const description = desc === "" ? undefined : desc;
                                            const flag = <Flag hoverable={false} size={18} marginTop={0} name={n.lng as ISO_3166} />;
                                            const isFallback = hasEnGbDescEntry ? n.lng === "en-GB" : j === 0;

                                            return (
                                                <React.Fragment key={j}>
                                                    {description !== undefined &&
                                                        <>
                                                            <T.TTFlag>{flag}</T.TTFlag>
                                                            <T.TTText style={{ fontWeight: isFallback ? "bolder" : "normal" }}>
                                                                {isFallback &&
                                                                    <Icon name="arrow right" style={{ float: "left", marginRight: 5 }} marginTop={1} size={12} color="@accentBlack" />
                                                                }
                                                                {desc}
                                                            </T.TTText>
                                                        </>
                                                    }
                                                </React.Fragment>
                                            );
                                        })}


                                    </div>
                                </>}
                            />
                        }
                        {t("description")}
                        {missing?.description &&
                            <Icon name="asterisk" color={"@accentRed"} marginTop={0} style={{ marginTop: 5, marginLeft: 3, marginRight: 5, float: "right" }} />
                        }
                    </AGCTitle>
                </div>
                <div>
                    <AGCInputTextArea>
                        <EditLanguageStringsBase
                            value={s_descriptions.value ?? []}
                            setValue={updateDescriptions}
                            editMode={true}
                            editorFormats={defEditorFormats["StandardDescription"]}
                            type="descriptions"
                            fixWidth={avlWidth}
                            initialOpen={!edit}
                            hideTitleBarIfEmpty
                            menuPortal={rootElement}
                            savingText={t('confirm description')}
                            savingIcon='check'
                        />
                    </AGCInputTextArea>
                </div>
                {expertMode &&
                    <AGCTitle>{t("categories")}</AGCTitle>
                }
                {expertMode &&
                    <CMSSC.Grid style={{ gridTemplateColumns: "max-content 1fr 0px", rowGap: 5 }}>
                        {_.map(toRender, r => (
                            <React.Fragment key={r.configuration.categoryType}>
                                <CMSSC.Label style={{ paddingRight: 10, paddingTop: 10 }}>
                                    {getTranslated(r.configuration.names)?.text ?? ""}
                                </CMSSC.Label>
                                <div>{r.Select}</div>
                                <div />
                            </React.Fragment>
                        ))}
                    </CMSSC.Grid>
                }
                <div>
                    <AGCTitle missingInput={missing?.media}>
                        {t("media")}
                        {missing?.media &&
                            <Icon name="asterisk" color={"@accentRed"} marginTop={0} style={{ marginTop: 5, marginLeft: 3, marginRight: 5, float: "right" }} />
                        }
                    </AGCTitle>
                </div>
                <div>
                    <AGCInputDate style={{ maxWidth: 271, background: "yellow" }}>
                        <MediaSelection
                            onMediaSelected={setMedia}
                            imgAspect={(16 / 9)}
                            uploadType={MediaType.IMAGE}
                            backgroundColor="#fff"
                            width="100%"
                            media={s_media.value}
                            hideButtonText={isMobile}
                            showPlaceholderImage={!isMobile}
                        />
                    </AGCInputDate>
                </div>
                {expertMode &&
                    <AGCTitle>
                        <LabelInfo
                            infoText=""
                            warning={false}
                            tooltipContent={<>
                                <div style={{
                                    fontWeight: "bolder", lineHeight: "20px", textAlign: "left", padding: 10, background: "rgba(0,0,0,0.05)", width: "calc(100% + 26px)", transform: "translate(-13px, -13px)"
                                }}>
                                    <Icon name="info" style={{ float: "left", marginRight: 10 }} marginTop={2} />
                                    {t("allow ratings")}
                                </div>
                                <div style={{ textAlign: "left", marginBottom: 10 }}>
                                    {t("allow users to rate the playlist")}
                                </div>
                            </>}
                        />
                        {t("allow ratings")}
                    </AGCTitle>
                }
                {expertMode &&
                    <AGCInput>
                        <SwitchButton
                            isActive={toggleDisallowRatings}
                            initialState={!s_disallowRatings.value}
                            floated='left'
                            size="default"
                            activeColor="@accentGreen"
                        />
                        <span style={{ marginLeft: 10 }}>
                            {s_disallowRatings.value ? t("disallowed") : t("allowed")}
                        </span>
                    </AGCInput>
                }
                {expertMode &&
                    <AGCTitle>
                        <LabelInfo
                            infoText=""
                            warning={false}
                            tooltipContent={<>
                                <div style={{
                                    fontWeight: "bolder", lineHeight: "20px", textAlign: "left", padding: 10, background: "rgba(0,0,0,0.05)", width: "calc(100% + 26px)", transform: "translate(-13px, -13px)"
                                }}>
                                    <Icon name="info" style={{ float: "left", marginRight: 10 }} marginTop={2} />
                                    {t("allow comments")}
                                </div>
                                <div style={{ textAlign: "left", marginBottom: 10 }}>
                                    {t("allow users to comment the playlist")}
                                </div>
                            </>}
                        />
                        {t("allow comments")}
                    </AGCTitle>
                }
                {expertMode &&
                    <AGCInput>
                        <SwitchButton
                            isActive={toggleAllowComments}
                            initialState={s_allowComments.value}
                            floated='left'
                            size="default"
                            activeColor="@accentGreen"
                        />
                        <span style={{ marginLeft: 10 }}>
                            {!s_allowComments.value ? t("disallowed") : t("allowed")}
                        </span>
                    </AGCInput>
                }



                {expertMode &&
                    <AGCTitle>{t("visibility")}</AGCTitle>
                }
                {expertMode &&
                    <AGCInput>
                        <SwitchButton
                            isActive={togglePublished}
                            initialState={!s_published.value}
                            floated='left'
                            size="default"
                            activeColor="@accentGreen"
                        />
                        <span style={{ marginLeft: 10 }}>
                            {!s_published.value ? t("visible") : t("invisible")}
                        </span>
                    </AGCInput>
                }
            </div>
        </T.ImplContainer>
    );
}
export default PlaylistEditGeneral;